import * as React from 'react'
import type { Language } from 'codegen/types'
import {
  canonicalizeStudioLocalePathname,
  isCanonicalStudioLocalePathname,
  studioLocaleSegmentForLanguage,
} from '@repo/react-router-locale/canonicalize-studio-locale-pathname'
import { historyReappliesLocalePrefix } from '@repo/react-router-locale/locale-history'
import { useStudioSetUrlLocaleSegment } from '@repo/studio-config/studio-config-context'
import {
  useStudioLocationState,
  useStudioNavigateToExact,
  useStudioRawMaskPathname,
  useStudioRawPathname,
} from '@repo/studio-config/studio-config-router'

interface UseSyncLocaleUrlOptions {
  /** When false, skips redirect until URL/account language resolution is complete. */
  enabled?: boolean
}

function buildPathWithLocationSuffix(pathname: string): string {
  if (typeof window === 'undefined') {
    return pathname
  }

  return `${pathname}${window.location.search}${window.location.hash}`
}

function useRewriteLocalePrefix() {
  // The canonical path is computed here, so it must be navigated to verbatim.
  // A locale-reapplying navigate cannot tell "unprefixed because English" from
  // "unprefixed because it came from ROUTES", and puts the old prefix back.
  const navigateTo = useStudioNavigateToExact()
  const locationState = useStudioLocationState()
  const rawMaskPathname = useStudioRawMaskPathname()
  const setUrlLocaleSegment = useStudioSetUrlLocaleSegment()

  // Under the history adapter the routes are locale-free, so the navigate branch
  // below sends the reader to a path no route matches — every localized URL
  // renders the 404 page, which reads as a routing bug rather than a wiring one.
  // Reaching here means this hook is mounted above `StudioConfigProvider`, or
  // the app never wired `setUrlLocaleSegment`.
  if (!setUrlLocaleSegment && historyReappliesLocalePrefix()) {
    throw new Error(
      'useSyncLocaleUrl needs setUrlLocaleSegment from StudioConfig when the locale history adapter is installed',
    )
  }

  return React.useCallback(
    (language: Language, pathname: string) => {
      // Where the router never sees the prefix, the prefix is the only thing
      // that has to change — the path the reader is on is already correct in
      // every language, so there is nothing to navigate to.
      if (setUrlLocaleSegment) {
        setUrlLocaleSegment(studioLocaleSegmentForLanguage(language))
        return
      }

      const pathnameIsCanonical = isCanonicalStudioLocalePathname(pathname, language)
      const maskIsCanonical =
        rawMaskPathname === undefined || isCanonicalStudioLocalePathname(rawMaskPathname, language)

      if (pathnameIsCanonical && maskIsCanonical) {
        return
      }

      const nextPathname = canonicalizeStudioLocalePathname(pathname, language)
      const nextMaskPathname = rawMaskPathname
        ? canonicalizeStudioLocalePathname(rawMaskPathname, language)
        : undefined

      // A client-side navigation, so changing language never reloads the page.
      navigateTo(buildPathWithLocationSuffix(nextPathname), {
        ...(nextMaskPathname ? { mask: nextMaskPathname } : {}),
        replace: true,
        state: locationState,
      })
    },
    [locationState, navigateTo, rawMaskPathname, setUrlLocaleSegment],
  )
}

/**
 * Keeps the URL locale prefix aligned with the effective account/cookie language.
 *
 * This also restores the prefix after an in-app navigation. `ROUTES` values are
 * unprefixed, so `<Link to={ROUTES.X}>` navigates to `/x` and this puts the
 * reader back on `/it/x`. It runs on every pathname change for that reason, and
 * uses `replace` so the intermediate entry never lands in history.
 *
 * Stored language preferences win over URL prefixes once set; URL locales only
 * seed language on first visit without a cookie.
 */
export function useSyncLocaleUrl(
  language: Language | null | undefined,
  options?: UseSyncLocaleUrlOptions,
) {
  const rawPathname = useStudioRawPathname()
  const rewriteLocalePrefix = useRewriteLocalePrefix()
  const enabled = options?.enabled ?? true

  React.useEffect(() => {
    if (!enabled || !language) {
      return
    }

    rewriteLocalePrefix(language, rawPathname)
  }, [enabled, language, rawPathname, rewriteLocalePrefix])
}

/**
 * Returns a function that rewrites the current URL to match a language, preserving
 * query string and hash. Intended for the language picker after i18n has updated.
 */
export function useNavigateToCanonicalLocaleUrl() {
  const rawPathname = useStudioRawPathname()
  const rewriteLocalePrefix = useRewriteLocalePrefix()

  return React.useCallback(
    (language: Language) => {
      rewriteLocalePrefix(language, rawPathname)
    },
    [rawPathname, rewriteLocalePrefix],
  )
}
